/**
 * QRT platform admin API — completely separate auth surface from the user app.
 * Token lives in localStorage "qrt_token"; 401 always returns to /qrt/login.
 */
import axios from "axios";
import { getApiBaseUrl } from "../config/apiBase";

const QRT_TOKEN_KEY = "qrt_token";
const QRT_ADMIN_KEY = "qrt_admin";

export const getQrtToken = () => localStorage.getItem(QRT_TOKEN_KEY);
export const getQrtAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem(QRT_ADMIN_KEY) || "null");
  } catch {
    return null;
  }
};
export const setQrtSession = (token, admin) => {
  localStorage.setItem(QRT_TOKEN_KEY, token);
  localStorage.setItem(QRT_ADMIN_KEY, JSON.stringify(admin || null));
};
export const clearQrtSession = () => {
  localStorage.removeItem(QRT_TOKEN_KEY);
  localStorage.removeItem(QRT_ADMIN_KEY);
};

const qrtApi = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { "Content-Type": "application/json" },
});

qrtApi.interceptors.request.use((config) => {
  const token = getQrtToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

qrtApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== "/qrt/login") {
      clearQrtSession();
      window.location.href = "/qrt/login";
    }
    return Promise.reject(error);
  }
);

/* Auth */
export const qrtLogin = (email, password) =>
  qrtApi.post("/api/qrt/auth/login", { email, password });
export const qrtMe = () => qrtApi.get("/api/qrt/me");
export const qrtChangePassword = (current_password, new_password) =>
  qrtApi.put("/api/qrt/me/password", { current_password, new_password });

/* Stats */
export const qrtOverview = () => qrtApi.get("/api/qrt/overview");
export const qrtTopAccounts = (page = 0) =>
  qrtApi.get("/api/qrt/overview/top-accounts", { params: { page } });
export const qrtRevenueByCompany = (params = {}) =>
  qrtApi.get("/api/qrt/revenue-by-company", { params });
export const qrtDownloadRevenueCsv = (params = {}) =>
  qrtApi.get("/api/qrt/revenue-by-company.csv", { params, responseType: "blob" });
export const qrtDownloadHistoryCsv = (params = {}) =>
  qrtApi.get("/api/qrt/revenue-history.csv", { params, responseType: "blob" });
export const qrtSetPlanRevenueExcluded = (userId, excluded) =>
  qrtApi.put(`/api/qrt/revenue-exclusions/${userId}`, { excluded });
export const qrtTimeseries = (days = 30) =>
  qrtApi.get("/api/qrt/timeseries", { params: { days } });

/* Users */
export const qrtListUsers = (params = {}) => qrtApi.get("/api/qrt/users", { params });
export const qrtGetUser = (id) => qrtApi.get(`/api/qrt/users/${id}`);
export const qrtCreateUser = (payload) => qrtApi.post("/api/qrt/users", payload);
export const qrtUpdateUser = (id, payload) => qrtApi.put(`/api/qrt/users/${id}`, payload);
export const qrtSetUserStatus = (id, status) =>
  qrtApi.post(`/api/qrt/users/${id}/status`, { status });
export const qrtDeleteUser = (id) => qrtApi.delete(`/api/qrt/users/${id}`);
export const qrtUserTransactions = (id, params = {}) =>
  qrtApi.get(`/api/qrt/users/${id}/transactions`, { params });
export const qrtSetWalletFrozen = (id, frozen) =>
  qrtApi.post(`/api/qrt/users/${id}/freeze`, { frozen });
export const qrtAdjustWallet = (id, amount, reason, options = {}) =>
  qrtApi.post(`/api/qrt/users/${id}/adjust`, {
    amount,
    reason,
    ...(options.includePlatformFee === undefined
      ? {}
      : { include_platform_fee: Boolean(options.includePlatformFee) }),
  });
export const qrtSetUserMarkups = (id, markups) =>
  qrtApi.put(`/api/qrt/users/${id}/markups`, markups);

/* Activity lists */
export const qrtListCampaigns = (params = {}) => qrtApi.get("/api/qrt/campaigns", { params });
export const qrtListFlows = (params = {}) => qrtApi.get("/api/qrt/flows", { params });
export const qrtListTemplates = (params = {}) => qrtApi.get("/api/qrt/templates", { params });
export const qrtListMessages = (params = {}) => qrtApi.get("/api/qrt/messages", { params });
export const qrtListTransactions = (params = {}) => qrtApi.get("/api/qrt/transactions", { params });
export const qrtServiceUsage = (params = {}) => qrtApi.get("/api/qrt/service-usage", { params });

/* Billing */
export const qrtGetGlobalMarkups = () => qrtApi.get("/api/qrt/billing/markups");
export const qrtSetGlobalMarkups = (markups) => qrtApi.put("/api/qrt/billing/markups", markups);
export const qrtListPricing = (params = {}) => qrtApi.get("/api/qrt/billing/pricing", { params });
export const qrtUpsertPrice = (payload) => qrtApi.post("/api/qrt/billing/pricing", payload);
export const qrtPricePreview = (payload) => qrtApi.post("/api/qrt/billing/price-preview", payload);
export const qrtListRecharges = (params = {}) => qrtApi.get("/api/qrt/billing/recharges", { params });

/* Plan catalog */
export const qrtListPlans = () => qrtApi.get("/api/qrt/plans");
export const qrtCreatePlan = (payload) => qrtApi.post("/api/qrt/plans", payload);
export const qrtUpdatePlan = (id, payload) => qrtApi.put(`/api/qrt/plans/${id}`, payload);
export const qrtListCreditPlans = () => qrtApi.get("/api/qrt/credit-plans");
export const qrtUpdateCreditPlan = (id, payload) => qrtApi.put(`/api/qrt/credit-plans/${id}`, payload);
export const qrtListAddonPlans = () => qrtApi.get("/api/qrt/addon-plans");
export const qrtCreateAddonPlan = (payload) => qrtApi.post("/api/qrt/addon-plans", payload);
export const qrtUpdateAddonPlan = (id, payload) => qrtApi.put(`/api/qrt/addon-plans/${id}`, payload);

/* Subscription access */
export const qrtGetUserAccess = (id) => qrtApi.get(`/api/qrt/users/${id}/access`);
export const qrtSetUserExempt = (id, exempt) => qrtApi.put(`/api/qrt/users/${id}/exempt`, { exempt });
export const qrtSetExtraNumbers = (id, extra_numbers) =>
  qrtApi.put(`/api/qrt/users/${id}/extra-numbers`, { extra_numbers });
export const qrtGrantSubscription = (id, payload) =>
  qrtApi.post(`/api/qrt/users/${id}/grant-subscription`, payload);
export const qrtCancelSubscription = (id) => qrtApi.post(`/api/qrt/users/${id}/cancel-subscription`);
export const qrtListSubscriptions = (params = {}) => qrtApi.get("/api/qrt/subscriptions", { params });

/* Per-user invoices (admin) */
export const qrtListUserInvoices = (id, params = {}) =>
  qrtApi.get(`/api/qrt/users/${id}/invoices`, { params });
export const qrtGetUserInvoice = (id, invoiceId) =>
  qrtApi.get(`/api/qrt/users/${id}/invoices/${invoiceId}`);
export const qrtUpdateUserInvoice = (id, invoiceId, payload) =>
  qrtApi.put(`/api/qrt/users/${id}/invoices/${invoiceId}`, payload);
export const qrtGetUserInvoicePdf = (id, invoiceId, download = false) =>
  qrtApi.get(`/api/qrt/users/${id}/invoices/${invoiceId}/pdf`, {
    params: download ? { download: 1 } : {},
    responseType: "blob",
  });
export const qrtDownloadUserInvoice = async (id, invoice, fallbackName) => {
  const res = await qrtGetUserInvoicePdf(id, invoice.id, true);
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const filename =
    res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] ||
    fallbackName ||
    `${invoice.invoice_number || `invoice-${invoice.id}`}.pdf`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const saveBlob = (res, fallbackName) => {
  const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const filename =
    res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || fallbackName;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* All invoices */
export const qrtListInvoices = (params = {}) => qrtApi.get("/api/qrt/invoices", { params });
export const qrtListInvoiceCompanies = () => qrtApi.get("/api/qrt/invoices/companies");
export const qrtApplyInvoiceBilling = (payload) => qrtApi.post("/api/qrt/invoices/apply-billing", payload);
export const qrtDownloadInvoicesCsv = async (params = {}) => {
  const res = await qrtApi.get("/api/qrt/invoices.csv", { params, responseType: "blob" });
  saveBlob(res, "ping-invoices.csv");
};
export const qrtDownloadInvoicesZip = async (params = {}) => {
  const res = await qrtApi.get("/api/qrt/invoices.zip", { params, responseType: "blob" });
  saveBlob(res, "ping-invoices.zip");
};

/* Funnel analytics */
export const qrtFunnel = (params = {}) => qrtApi.get("/api/qrt/funnel", { params });

/* Audit */
export const qrtListAuditLog = (params = {}) => qrtApi.get("/api/qrt/audit-log", { params });

export default qrtApi;
